import { Button, Grid, TextField, MenuItem, IconButton, DialogActions } from '@mui/material';
import React, { useState, useEffect, useContext } from 'react';
import { AlertMessagesContext } from 'react-alert-messages';
import styles from './styles.module.css';
import CloseIcon from '@mui/icons-material/Close';
import VendorServices from 'services/VendorServices';
import NumberField from 'components/library/NumberField';
import { useTranslation } from 'react-i18next';

export default function NewVendorDialog({ onSuccess, onClose, initialName = '', initialMobile = '' }) {
  const { t: tCommon } = useTranslation('common');
  const { t: tPopup } = useTranslation('popup');

  const TYPE = {
    CREDIT: 'Credit',
    ADVANCE: 'Advance',
  };

  const [name, setName] = useState(initialName || '');
  const [mobile, setMobile] = useState(initialMobile || '');
  const [code, setCode] = useState('');
  const [balance, setBalance] = useState('');
  const [address, setAddress] = useState('');
  const [nameError, setNameError] = useState(false);
  const [mobileError, setMobileError] = useState(false);
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const [balanceType, setBalanceType] = useState(TYPE.CREDIT);

  useEffect(() => {
    if (initialName) {
      setName(initialName);
    }
    if (initialMobile) {
      setMobile(initialMobile);
    }
  }, [initialName, initialMobile]);

  const createNewVendor = async () => {
    let hasError = false;
    if (!name || !name.trim()) {
      setNameError(true);
      postAlertMessage({
        text: tPopup('Vendor Name is a required mandatory field.'),
        type: 'failed',
      });
      hasError = true;
    }
    if (!mobile || !mobile.trim()) {
      setMobileError(true);
      postAlertMessage({
        text: tPopup('Vendor Mobile is a required mandatory field.'),
        type: 'failed',
      });
      hasError = true;
    }

    if (hasError) return;

    const amt = Number(balance) || 0;
    const adjustedBalance = balanceType === TYPE.CREDIT ? -Math.abs(amt) : Math.abs(amt);
    const finalCode = code.trim() || `VND-${Math.floor(10000 + Math.random() * 90000)}`;

    const data = {
      name: name.trim(),
      mobile: mobile.trim(),
      code: finalCode,
      balance: adjustedBalance,
    };
    if (address && address.trim()) {
      data.address = address.trim();
    }

    setLoadingIndicator(true);
    try {
      await VendorServices.createVendor(data);
      postAlertMessage({
        text: tPopup('Vendor created successfully'),
        type: 'success',
      });
      setLoadingIndicator(false);
      onSuccess();
    } catch (err) {
      setLoadingIndicator(false);

      let dataObj = err?.response?.data || err?.data || err;
      if (dataObj && typeof dataObj === 'object' && dataObj.message) {
        dataObj = dataObj.message;
      }
      if (typeof dataObj === 'string') {
        try {
          if (dataObj.trim().startsWith('{') || dataObj.trim().startsWith('[')) {
            dataObj = JSON.parse(dataObj);
          }
        } catch (e) {
          // Keep string if not JSON
        }
      }

      let errorMsg = 'Failed to create vendor';
      if (typeof dataObj === 'string') {
        errorMsg = dataObj;
      } else if (typeof dataObj === 'object' && dataObj !== null) {
        if (dataObj.detail) errorMsg = String(dataObj.detail);
        else if (dataObj.message) errorMsg = String(dataObj.message);
        else {
          const parts = [];
          Object.keys(dataObj).forEach((k) => {
            if (['stack', 'config', 'headers', 'request'].includes(k)) return;
            const fieldLabel = k.charAt(0).toUpperCase() + k.slice(1).replace(/_/g, ' ');
            const val = dataObj[k];
            if (Array.isArray(val)) parts.push(`${fieldLabel}: ${val.join(', ')}`);
            else if (typeof val === 'string') parts.push(`${fieldLabel}: ${val}`);
          });
          if (parts.length > 0) errorMsg = parts.join(' | ');
          else errorMsg = err.message || errorMsg;
        }
      }
      postAlertMessage({
        text: errorMsg,
        type: 'failed',
      });
    }
  };

  return (
    <div className={styles.popUp} style={{ zIndex: 12000 }}>
      <div className={styles.contentWrapper}>
        <div className={styles.headSec}>
          <h2 className={styles.editTitle}>{tCommon('Create New Vendor')}</h2>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>
        <div className={styles.inputSec}>
          <Grid container className={styles.inputRow} alignItems="top" spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                error={nameError}
                helperText={nameError ? tPopup('Vendor Name is required') : ''}
                label={tCommon('Vendor Name')}
                variant="outlined"
                size="small"
                name="name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (e.target.value.trim()) setNameError(false);
                }}
                fullWidth
                autoFocus
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                error={mobileError}
                helperText={mobileError ? tPopup('Vendor Mobile is required') : ''}
                label={tCommon('Vendor Mobile')}
                variant="outlined"
                size="small"
                name="mobile"
                placeholder="Enter mobile number"
                value={mobile}
                onChange={(e) => {
                  setMobile(e.target.value);
                  if (e.target.value.trim()) setMobileError(false);
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={tCommon('Vendor Code')}
                variant="outlined"
                size="small"
                name="code"
                placeholder="Optional (Auto-generated)"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <TextField
                    select
                    label={tCommon('Balance Type')}
                    variant="outlined"
                    size="small"
                    value={balanceType}
                    onChange={(e) => setBalanceType(e.target.value)}
                    className={styles.selectBox}
                    fullWidth
                  >
                    <MenuItem value={TYPE.CREDIT}>{tCommon('Credit')}</MenuItem>
                    <MenuItem value={TYPE.ADVANCE}>{tCommon('Advance')}</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={8}>
                  <NumberField
                    label={tCommon('Vendor Balance')}
                    className={styles.numberInput}
                    value={balance}
                    onChange={(e) => setBalance(e.target.value)}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={tCommon('Vendor Address')}
                variant="outlined"
                size="small"
                name="address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                fullWidth
                multiline
                maxRows={4}
              />
            </Grid>
            <Grid item xs={12} sm={6}></Grid>
          </Grid>
        </div>
        <div className={styles.footSec}>
          <DialogActions>
            <Button variant="contained" color="secondary" size="small" onClick={onClose}>
              {tCommon('Close')}
            </Button>
            <Button
              variant="contained"
              size="small"
              color="primary"
              style={{ backgroundColor: '#00a65a' }}
              disabled={!name.trim() || loadingIndicator}
              onClick={createNewVendor}
            >
              {tCommon('Create Vendor')}
            </Button>
          </DialogActions>
        </div>
      </div>
    </div>
  );
}
